if (!customElements.get('pickup-availability')) {
  customElements.define(
    'pickup-availability',
    class PickupAvailability extends HTMLElement {
      constructor() {
        super();

        this.errorHtml = this.querySelector('template').content.firstElementChild.cloneNode(true);
        this.onClickRefreshList = this.onClickRefreshList.bind(this);
        this.drawer = document.getElementById('drawer-pickup-availability');
        this.mountDrawerToBody();

        if (this.dataset.variantId) {
          this.fetchAvailability(this.dataset.variantId);
        }
      }

      fetchAvailability(variantId) {
        this.dataset.variantId = String(variantId);
        this.fetchVariantAvailabilityData(variantId)
          .then((sectionVariantData) => {
            if (!sectionVariantData || !Array.isArray(sectionVariantData.locations) || sectionVariantData.locations.length === 0) {
              this.renderUnavailable();
              this.renderDrawer([]);
              return;
            }

            this.renderPreview(sectionVariantData.locations);
          })
          .catch(() => {
            this.renderError();
            this.renderDrawer([]);
          });
      }

      onClickRefreshList(evt) {
        this.fetchAvailability(this.dataset.variantId);
      }

      renderError() {
        if (!this.errorHtml) return;

        this.innerHTML = '';
        this.appendChild(this.errorHtml);

        this.querySelector('button').addEventListener('click', this.onClickRefreshList);
      }

      renderUnavailable() {
        const iconMarkup = `<svg role="presentation" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="feather feather-x-circle">
          <circle cx="12" cy="12" r="10"></circle>
          <line x1="15" y1="9" x2="9" y2="15"></line>
          <line x1="9" y1="9" x2="15" y2="15"></line>
        </svg>`;

        this.innerHTML = `
          <pickup-availability-preview class="pickup-availability-preview">
            ${iconMarkup}
            <div class="pickup-availability-info">
              <p class="pickup-availability-preview__line">${this.dataset.pickupUnavailableLabel}</p>
            </div>
          </pickup-availability-preview>
        `;
        this.setAttribute('available', '');
      }

      fetchVariantAvailabilityData(variantId) {
        let rootUrl = this.dataset.rootUrl || '/';
        if (!rootUrl.endsWith('/')) {
          rootUrl = `${rootUrl}/`;
        }
        const variantSectionUrl = `${rootUrl}variants/${variantId}/?section_id=pickup-availability`;

        return fetch(variantSectionUrl)
          .then((response) => response.text())
          .then((text) => {
            const parsedDocument = new DOMParser().parseFromString(text, 'text/html');
            const sectionDataNode = parsedDocument.querySelector('[data-pickup-availability-section-data]');
            if (!sectionDataNode) return null;

            return JSON.parse(sectionDataNode.textContent);
          });
      }

      mountDrawerToBody() {
        if (!this.drawer) return;
        if (this.drawer.parentElement === document.body) return;

        // Keep drawer outside section wrappers so fixed positioning/z-index works consistently on desktop.
        document.body.appendChild(this.drawer);
      }

      sortAvailableLocationsFirst(locations) {
        return [...locations].sort((locationA, locationB) => {
          if (locationA.available === locationB.available) return 0;

          return locationA.available ? -1 : 1;
        });
      }

      renderPreview(locations) {
        if (!locations.length) {
          this.innerHTML = '';
          this.removeAttribute('available');
          this.renderDrawer([]);
          return;
        }

        const prioritizedLocations = this.sortAvailableLocationsFirst(locations);
        const previewLocation = prioritizedLocations[0];
        const isPreviewAvailable = Boolean(previewLocation.available);
        const detailsLinkLabel = locations.length === 1
          ? this.dataset.viewStoreInformationLabel
          : this.dataset.checkOtherStoresLabel;

        const iconMarkup = isPreviewAvailable
          ? `<svg role="presentation" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="feather feather-check-circle">
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
              <polyline points="22 4 12 14.01 9 11.01"></polyline>
            </svg>`
          : `<svg role="presentation" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="feather feather-x-circle">
              <circle cx="12" cy="12" r="10"></circle>
              <line x1="15" y1="9" x2="9" y2="15"></line>
              <line x1="9" y1="9" x2="15" y2="15"></line>
            </svg>`;
        const previewText = isPreviewAvailable
          ? previewLocation.pickupAvailableAt
          : this.dataset.pickupUnavailableLabel;
        const detailsLinkMarkup = isPreviewAvailable
          ? `<a href="#drawer-pickup-availability" class="pickup-availability-button link" aria-haspopup="dialog">
                ${detailsLinkLabel}
              </a>`
          : '';

        this.innerHTML = `
          <pickup-availability-preview class="pickup-availability-preview">
            ${iconMarkup}
            <div class="pickup-availability-info">
              <p class="pickup-availability-preview__line">${previewText}</p>
              ${detailsLinkMarkup}
            </div>
          </pickup-availability-preview>
        `;
        this.setAttribute('available', '');
        this.renderDrawer(prioritizedLocations);

        const previewButton = this.querySelector('a[href*="drawer"]');
        if (previewButton) {
          const target = previewButton.getAttribute('href').replace('#', '');
          previewButton.addEventListener('click', (event) => {
            event.preventDefault();
            Woolman.ModalsAndDrawers.showModalOrDrawer(target);
          });
        }
      }

      renderDrawer(locations) {
        if (!this.drawer) return;

        const contentNode = this.drawer.querySelector('[data-store-availability-drawer-content]');
        if (!contentNode) return;

        if (!locations.length) {
          contentNode.innerHTML = '';
          return;
        }

        const useNewLayout = this.dataset.useNewLayout !== 'false';

        contentNode.innerHTML = locations.map((location) => {
          const imageSizes = useNewLayout
            ? '(max-width: 699px) 327px, 325px'
            : '(max-width: 699px) 159px, 166px';
          const availabilityLabel = location.available
            ? `${this.dataset.pickupAvailableLabel}, ${location.pickUpTime}`
            : this.dataset.pickupUnavailableLabel;
          const mapEmbed = location.mapIframe
            ? `<div class="pickup-availability-map" aria-hidden="true">${location.mapIframe}</div>`
            : '';
          const routeLink = location.routeUrl
            ? `<a class="pickup-availability-route-link link" href="${location.routeUrl}" target="_blank" rel="noopener noreferrer">${this.dataset.showRouteLabel}</a>`
            : '';
          const storeImage = location.shopImageUrl
            ? `<div class="pickup-availability-image-wrap"><img class="pickup-availability-image" src="${location.shopImageUrl}" srcset="${this.escapeHtml(location.shopImageSrcSet || '')}" sizes="${imageSizes}" alt="${this.escapeHtml(location.shopImageAlt || location.name)}" loading="lazy" decoding="async"></div>`
            : '';
          const storeOpeningTimesLabel = this.escapeHtml(this.dataset.storeOpeningTimesLabel || 'Store Opening Times');
          const additionalInfo = location.additionalInfo || '';
          const storeAddressLabel = this.escapeHtml(this.dataset.storeAddressLabel || 'Store Address');

          if (!useNewLayout) {
            const oldStoreMedia = storeImage
              ? `<div class="pickup-availability-main__media pickup-availability-main__media--legacy">${storeImage}</div>`
              : '';
            const oldOpeningTimes = additionalInfo
              ? `
                <p class="pickup-availability-address-title caption">${storeOpeningTimesLabel}</p>
                <div class="pickup-availability-extra-info__content rte">${additionalInfo}</div>
              `
              : '';

            return `
              <li class="pickup-availability-list__item">
                <p class="pickup-availability-preview">
                  <span class="ping ${location.available ? 'green' : 'red'}"></span>
                  ${this.escapeHtml(location.name)}
                </p>
                <p class="pickup-availability-preview caption pickup-availability-status-text">${this.escapeHtml(availabilityLabel)}</p>
                <div class="pickup-availability-main${oldStoreMedia ? '' : ' pickup-availability-main--no-media'}">
                  ${oldStoreMedia}
                  <div class="pickup-availability-main__info">
                    <p class="pickup-availability-address-title caption">${storeAddressLabel}</p>
                    <address class="pickup-availability-address">${location.addressHtml || ''}</address>
                    ${oldOpeningTimes}
                    ${routeLink}
                  </div>
                </div>
                ${mapEmbed}
              </li>
            `;
          }

          return `
            <li class="pickup-availability-list__item">
              <p class="pickup-availability-preview">
                <span class="ping ${location.available ? 'green' : 'red'}"></span>
                ${this.escapeHtml(location.name)}
              </p>
              <p class="pickup-availability-preview caption pickup-availability-status-text">${this.escapeHtml(availabilityLabel)}</p>
              <div class="pickup-availability-text-block${additionalInfo ? '' : ' pickup-availability-text-block--single'}">
                <div class="pickup-availability-info-column">
                  <p class="pickup-availability-address-title caption">${storeAddressLabel}</p>
                  <address class="pickup-availability-address">${location.addressHtml || ''}</address>
                  ${routeLink}
                </div>
                ${additionalInfo ? `
                  <div class="pickup-availability-info-column">
                    <p class="pickup-availability-address-title caption">${storeOpeningTimesLabel}</p>
                    <div class="pickup-availability-extra-info__content rte">${additionalInfo}</div>
                  </div>
                ` : ''}
              </div>
              ${storeImage}
              ${mapEmbed}
            </li>
          `;
        }).join('');
      }

      escapeHtml(value = '') {
        return String(value)
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/"/g, '&quot;')
          .replace(/'/g, '&#39;');
      }
    }
  );
}

if (!customElements.get('pickup-availability-drawer')) {
  customElements.define('pickup-availability-drawer', class PickupAvailabilityDrawer extends HTMLElement {
    constructor() {
      super();

      const closeBtns = this.querySelectorAll('[data-close]');
      if (closeBtns) {
        closeBtns.forEach((closeBtn) => {
          closeBtn.addEventListener('click', Woolman.ModalsAndDrawers.closeModalOrDrawerOrDrawerFromEvent);
        });
      }

      const overlay = this.querySelector('[data-overlay]');
      if (overlay) {
        overlay.addEventListener('click', Woolman.ModalsAndDrawers.closeModalOrDrawerOrDrawerFromEvent);
      }
    }
  });
}
